// components/PhaseCard.tsx
import { useState } from 'react';
import type { PhaseAnalysis } from '../balancing.types';
import { formatStatValue, getStatColor } from '../balancing.utils';
import { MessageRow } from './MessageRow';

type Props = {
  phase: PhaseAnalysis;
  phaseNumber: number;
};

export function PhaseCard({ phase, phaseNumber }: Props) {
  const [expanded, setExpanded] = useState(false);

  const stats = [
    { label: 'Cash', key: 'cash' as const },
    { label: 'Investments', key: 'investments' as const },
    { label: 'Happiness', key: 'happiness' as const },
  ];

  return (
      <div className="bg-white border-2 border-gray-200 rounded-lg p-6 mb-4">
        {/* Phase header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold">
              Phase {phaseNumber}: {phase.phaseName}
            </h3>
            <p className="text-sm text-gray-500">{phase.messageCount} messages</p>
          </div>
          <button
              onClick={() => setExpanded(!expanded)}
              className="cursor-pointer px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 border border-blue-600 rounded hover:bg-blue-50"
          >
            {expanded ? 'Collapse' : 'Expand'} Messages
          </button>
        </div>

        {/* Phase stats table */}
        <table className="w-full mb-4">
          <thead>
          <tr className="border-b border-gray-200">
            <th className="text-left py-2 px-3 text-sm font-semibold text-gray-700">Stat</th>
            <th className="text-center py-2 px-3 text-sm font-semibold text-gray-700">Min</th>
            <th className="text-center py-2 px-3 text-sm font-semibold text-gray-700">Avg</th>
            <th className="text-center py-2 px-3 text-sm font-semibold text-gray-700">Max</th>
          </tr>
          </thead>
          <tbody>
          {stats.map((stat) => (
              <tr key={stat.key} className="border-b border-gray-100 last:border-0">
                <td className="py-2 px-3 text-sm font-medium text-gray-900">{stat.label}</td>
                <td className="py-2 px-3 text-center">
                <span className={`inline-block px-2 py-0.5 rounded text-sm font-bold ${getStatColor(phase.min[stat.key])}`}>
                  {formatStatValue(phase.min[stat.key])}
                </span>
                </td>
                <td className="py-2 px-3 text-center">
                <span className={`inline-block px-2 py-0.5 rounded text-sm font-bold ${getStatColor(phase.avg[stat.key])}`}>
                  {formatStatValue(phase.avg[stat.key])}
                </span>
                </td>
                <td className="py-2 px-3 text-center">
                <span className={`inline-block px-2 py-0.5 rounded text-sm font-bold ${getStatColor(phase.max[stat.key])}`}>
                  {formatStatValue(phase.max[stat.key])}
                </span>
                </td>
              </tr>
          ))}
          </tbody>
        </table>

        {/* Message breakdown */}
        {expanded && (
            <div className="space-y-3 pt-4 border-t border-gray-200">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Message Breakdown</h4>
              {phase.messages.map((message) => (
                  <MessageRow key={message.messageIndex} message={message} />
              ))}
            </div>
        )}
      </div>
  );
}